"use client";

import React, { Suspense } from "react";
import { Button } from "@/components/ui/button";
import { HelpCircle, Menu, Bookmark, SearchIcon, Flag } from "lucide-react";
import AnimatedLogo from "./AnimatedLogo";
import useHeader from "@/hooks/useHeader";
import ProfileDropdown from "./ProfileDropdown";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useRouter } from "nextjs-toploader/app";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import PlanaTriangleIcon from "@/components/ui/icons/PlanaTriangleIcon";
import DashBoardIcon from "@/components/ui/icons/DashBoardIcon";
import ProjectsIcon from "@/components/ui/icons/ProjectsIcon";
import MessagesIcon from "@/components/ui/icons/MesssagesIcon";
import { useProfileData } from "@/components/hooks/profile/useProfileData.hook";
import ReviewMenuIcon from "@/components/ui/icons/ReviewMenuIcon";
import ContractorNotificationDropdown from "./contractor-notification-dropdown";
import HomeOwnerNotificationDropdown from "./homeowner-notification-dropdown";
import { useNotificationSocket } from "@/hooks/notification/useNotificationSocket.hooks";
import { useNotificationCount } from "@/hooks/notification/useNotificationCount.hooks";
import { useMessageNotificationCount } from "@/hooks/notification/useMessageNotificationCount.hooks";
import ChatNotificationWrapper from "./ChatNotificationWrapper";
import HomeownerMessageDropdown from "./homeowner-message-dropdown";
import ContractorMessageDropdown from "./contractor-message-dropdown";
import { useChatUnreadStore } from "@/store/chat-unread-store";

interface NavigationItem {
  name: string;
  href: string;
  icon: any;
  disabled?: boolean;
}

const homeownerNavigationItems: NavigationItem[] = [
  {
    name: "Dashboard",
    href: "/homeowner",
    icon: DashBoardIcon,
  },
  {
    name: "Projects",
    href: "/homeowner/projects",
    icon: ProjectsIcon,
  },
  {
    name: "Messages",
    href: "/homeowner/messages",
    icon: MessagesIcon,
  },
];

const contractorNavigationItems: NavigationItem[] = [
  {
    name: "Dashboard",
    href: "/contractor",
    icon: DashBoardIcon,
  },
  {
    name: "My Projects",
    href: "/contractor/projects",
    icon: ProjectsIcon,
  },
  {
    name: "Messages",
    href: "/contractor/messages",
    icon: MessagesIcon,
  },
  {
    name: "Reviews",
    href: "/contractor/reviews",
    icon: ReviewMenuIcon,
  },
  {
    name: "Bookmarks",
    href: "/contractor/bookmarks",
    icon: Bookmark,
  },
];

// Internal moderator nav (Apple Guideline 1.2 — content reports console).
const adminNavigationItems: NavigationItem[] = [
  {
    name: "Reports",
    href: "/reports",
    icon: Flag,
  },
];

const contractorRouteContent: Record<
  string,
  { title: string; subtitle: string | ((name: string) => string) }
> = {
  "/contractor": {
    title: "Dashboard",
    subtitle: (name: string) => `Welcome to Plana, ${name}!`,
  },
  "/contractor/projects": {
    title: "My Projects",
    subtitle: " Let's see what's happening around!",
  },
  "/contractor/messages": {
    title: "Messages",
    subtitle: "Let’s see what’s happening around!",
  },
  "/contractor/reviews": {
    title: "Reviews",
    subtitle: "Let's see what's happening around!",
  },
  "/contractor/bookmarks": {
    title: "Bookmarks",
    subtitle:
      "Here you 'll find all your saved projects. Some of them might be expired",
  },
};

function HeaderInternal({ hasUnreadMessages, clearNotification }: { hasUnreadMessages: boolean; clearNotification: () => void }) {
  const router = useRouter();
  const pathname = usePathname();
  const {
    isLoggedIn,
    deviceWidth,
    isOpen,
    setIsOpen,
    isActive,
    isAuthPage,
    userType,
  } = useHeader();
  const { profileData } = useProfileData();

  // Initialize SignalR connection for notifications
  useNotificationSocket(isLoggedIn, userType);

  // Fetch initial notification counts from socket cache
  const { data: unreadCount } = useNotificationCount(isLoggedIn);
  const { data: messageUnreadCount } = useMessageNotificationCount(isLoggedIn);

  // Chat notifications will be handled by ChatNotificationWrapper

  const isContractor = userType === "Contractor";
  // Moderators are identified server-side by role/permission, not by the
  // Homeowner/Contractor UserType — so detect via a forward-compatible string
  // compare. The /reports route is protected by the auth middleware and the
  // backend [Permission] regardless of nav visibility.
  const isAdmin = (userType as string | undefined) === "Admin";
  const projectCreateUrl = isContractor
    ? "/contractor/browse-project"
    : "/homeowner/projects/create";

  // Detect contractor browse projects page
  const isContractorBrowseProjectPage =
    isContractor && pathname.startsWith("/contractor/browse-project");

  // Navigation items used in mobile sheet
  const navigationItems = isAdmin
    ? adminNavigationItems
    : isContractor
      ? contractorNavigationItems
      : homeownerNavigationItems;

  // Only apply contractor layout when logged in AND is contractor AND not on auth page
  // AND not on contractor browse projects page (which should use homeowner-style header)
  const shouldApplyContractorLayout =
    isLoggedIn && isContractor && !isAuthPage && !isContractorBrowseProjectPage;

  // Get current route content for contractor
  const getCurrentRouteContent = () => {
    if (!isContractor) return null;

    // Find exact match first
    if (contractorRouteContent[pathname]) {
      return contractorRouteContent[pathname];
    }

    const matchingRoute = Object.keys(contractorRouteContent).find(
      (route) => pathname.startsWith(route) && route !== "/contractor"
    );

    return matchingRoute
      ? contractorRouteContent[matchingRoute]
      : contractorRouteContent["/contractor"];
  };

  const currentRouteContent = getCurrentRouteContent();
  const getSubtitle = () => {
    if (!currentRouteContent) return "";
    const { subtitle } = currentRouteContent;
    if (typeof subtitle === "function") {
      return subtitle(profileData?.name || "there");
    }
    return subtitle;
  };

  return (
    <header
      className={cn(
        "h-[72px] px-5 md:px-8 flex items-center justify-between border-b border-border bg-background z-9 fixed top-0 w-full",
        deviceWidth !== null &&
          deviceWidth >= 1200 &&
          shouldApplyContractorLayout &&
          "left-[330px] w-[calc(100%-330px)] bg-transparent border-none relative"
      )}
    >
      {/* Logo and Navigation */}
      <div className="flex justify-between items-center gap-2 xl:gap-3">
        {deviceWidth !== null &&
          deviceWidth < 1200 &&
          !isAuthPage &&
          isLoggedIn && (
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="h-10 w-10"
                  size="lg"
                  onClick={() => setIsOpen(!isOpen)}
                  data-testid="mobile-menu-trigger"
                >
                  <Menu className="!h-5 !w-5" />
                  <span className="sr-only">Toggle navigation menu</span>
                </Button>
              </SheetTrigger>
              <SheetTitle></SheetTitle>
              <SheetContent
                side="left"
                className="w-full !max-w-full z-0 top-[72px]"
                SheetOverlayClassName="hidden"
                hideCloseButton
              >
                <div className="flex flex-col gap-4 py-4 px-4 pt-[35px]">
                  {!isContractor && (
                    <Button
                      variant="outline"
                      size="lg"
                      className="gap-[10px] h-[42px] bg-transparent !pl-[26px] !pr-8 sm:hidden"
                      data-tour="start-project-button"
                      onClick={() => {
                        setIsOpen(false);
                        router.push(projectCreateUrl);
                      }}
                    >
                      <PlanaTriangleIcon className="!w-[21px] !h-[20px]" />
                      Start a Project
                    </Button>
                  )}

                  {isContractor && (
                    <Button
                      variant="outline"
                      size="lg"
                      className="gap-[10px] h-[42px] bg-transparent !pl-[26px] !pr-8 sm:hidden"
                      data-tour="start-project-button"
                      style={{
                        display:
                          deviceWidth !== null &&
                          deviceWidth < 1200 &&
                          !isAuthPage &&
                          isLoggedIn
                            ? "inline-flex"
                            : "none",
                      }}
                      onClick={() => {
                        setIsOpen(false);
                        router.push(projectCreateUrl);
                      }}
                    >
                      <SearchIcon className="!w-5 !h-5" />
                      Browse Projects
                    </Button>
                  )}
                  <nav className="flex flex-col gap-1">
                    {navigationItems.map((item) => {
                      return (
                        <Button
                          key={item.name}
                          variant="ghost"
                          disabled={item.disabled}
                          className={cn(
                            "justify-start gap-[10px] h-10 text-mantine hover:text-black hover:bg-[#f2f4f7]",
                            isActive(item.href) && "bg-accent text-foreground",
                            item.disabled && "opacity-50 cursor-not-allowed"
                          )}
                          onClick={() => {
                            if (!item.disabled) {
                              if (item.name === "Messages") {
                                clearNotification();
                              }
                              setIsOpen(false);
                              router.push(item.href);
                            }
                          }}
                          data-testid={`mobile-nav-${item.name
                            .toLowerCase()
                            .replace(" ", "-")}`}
                        >
                          <div className="relative">
                            <item.icon className="!h-6 !w-6" />
                            {item.name === "Messages" && hasUnreadMessages && (
                              <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5 rounded-full bg-primary" />
                            )}
                          </div>
                          {item.name}
                        </Button>
                      );
                    })}
                  </nav>
                </div>
              </SheetContent>
            </Sheet>
          )}
        {/* Hide logo on desktop for contractors when logged in (it's in the sidebar) */}
        {!(
          deviceWidth !== null &&
          deviceWidth >= 1200 &&
          shouldApplyContractorLayout
        ) && <AnimatedLogo width={130} height={24} className="w-[130px] h-6" />}
        {deviceWidth !== null &&
          deviceWidth > 1200 &&
          isLoggedIn === true &&
          (!isContractor || isContractorBrowseProjectPage) && (
            <nav className="flex items-center ml-8">
              {(isContractor && isContractorBrowseProjectPage
                ? contractorNavigationItems
                : homeownerNavigationItems
              ).map((item) => (
                <Button
                  key={item.name}
                  variant="ghost"
                  size={"lg"}
                  disabled={item.disabled}
                  className={cn(
                    "border border-transparent h-[42px] px-4 leading-[16px] font-semibold text-muted-foreground hover:text-muted-foreground",
                    isActive(item.href) && "bg-accent text-foreground",
                    item.disabled && "opacity-50 cursor-not-allowed"
                  )}
                  onClick={() => {
                    if (!item.disabled) {
                      if (item.name === "Messages") {
                        clearNotification();
                      }
                      router.push(item.href);
                    }
                  }}
                  data-testid={`desktop-nav-${item.name.toLowerCase()}`}
                >
                  <div className="relative flex items-center gap-2">
                    {item.name}
                    {item.name === "Messages" && hasUnreadMessages && (
                      <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                    )}
                  </div>
                </Button>
              ))}
            </nav>
          )}
      </div>

      {/* Need Help Button / User Actions */}
      {isLoggedIn === null ? (
        <div className="flex items-center gap-[10px]">
          <div className="w-11 h-11 bg-gray-200 rounded animate-pulse" />
          <div className="w-32 h-11 bg-gray-200 rounded animate-pulse" />
        </div>
      ) : !isLoggedIn ? (
        <Button
          variant="outline"
          size="lg"
          className="!sm:px-4 px-2"
          onClick={() => router.push("https://www.planax.co/contact-us")}
        >
          <HelpCircle className="!w-6 !h-6" />
          <span className="inline">Need Help?</span>
        </Button>
      ) : (
        <div
          className={cn(
            "flex items-center gap-[10px]",
            deviceWidth !== null &&
              deviceWidth >= 1200 &&
              isContractor &&
              !isContractorBrowseProjectPage &&
              isLoggedIn &&
              !isAuthPage &&
              "flex-1 pl-[30px] pt-[30px]"
          )}
        >
          {/* Dynamic route content for contractor on desktop */}
          {isContractor &&
            currentRouteContent &&
            deviceWidth !== null &&
            deviceWidth >= 1200 &&
            !isContractorBrowseProjectPage && (
              <div
                className="mr-auto mt-4"
                data-testid="contractor-route-content"
              >
                <h1 className="text-3xl leading-[39px] font-semibold text-gray-900">
                  {currentRouteContent.title}
                </h1>
                <p className="text-gray-600">{getSubtitle()}</p>
              </div>
            )}

          {isContractor ? (
            <div className="flex items-center gap-[10px] contractor">
              <div
                data-tour="contractor-header-actions"
                className="flex items-center gap-[10px]"
              >
                {/* Notification bell */}
                <ContractorMessageDropdown unreadCount={messageUnreadCount} />
                <ContractorNotificationDropdown unreadCount={unreadCount} />

                {/* Browse Projects button for contractor */}
                <Button
                  variant="outline"
                  size="lg"
                  className="gap-[10px] h-[42px] bg-transparent !pl-[26px] !pr-8  hidden sm:inline-flex"
                  data-testid="browse-projects-button"
                  style={{
                    display:
                      deviceWidth !== null &&
                      deviceWidth >= 1200 &&
                      !isAuthPage &&
                      isLoggedIn &&
                      !isContractorBrowseProjectPage
                        ? "inline-flex"
                        : "none",
                  }}
                  onClick={() => router.push("/contractor/browse-project")}
                >
                  <SearchIcon className="!w-5 !h-5" />
                  Browse Projects
                </Button>
              </div>
              {/*Profile Dropdown*/}
              <ProfileDropdown />
            </div>
          ) : (
            <div className="flex items-center gap-[10px]">
              {/* Message Icon */}
               <HomeownerMessageDropdown unreadCount={messageUnreadCount} />
              {/* Notification bell */}
              <HomeOwnerNotificationDropdown unreadCount={unreadCount} />
              <Button
                variant="outline"
                size="lg"
                className="gap-[10px] h-[42px] bg-transparent !pl-[26px] !pr-8 mr-6 hidden sm:inline-flex"
                data-tour="start-project-button"
                onClick={() => router.push(projectCreateUrl)}
              >
                <PlanaTriangleIcon className="!w-[21px] !h-[20px]" />
                Start a Project
              </Button>

              <ProfileDropdown />
            </div>
          )}
        </div>
      )}
    </header>
  );
}

export function Header() {
  return (
    <ChatNotificationWrapper>
      {(hasUnreadMessages, clearNotification) => (
        <HeaderInternal hasUnreadMessages={hasUnreadMessages} clearNotification={clearNotification} />
      )}
    </ChatNotificationWrapper>
  );
}
